import React, { useEffect, useState, useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { 
  Card, 
  CardContent, 
  Grid, 
  Typography, 
  Box, 
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Avatar,
  AvatarGroup,
  alpha,
  useTheme,
  Alert,
  Badge as MuiBadge
} from '@mui/material';
import {
  TrendingUp,
  School,
  Schedule,
  CreditCard,
  Print,
  WhatsApp,
  Assessment,
  EmojiEvents,
  PlayArrow,
  Bookmark,
  People,
  ChevronRight,
  Notifications,
  Celebration,
  Announcement,
  Event,
  Warning,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { Spinner } from "react-bootstrap";
import { Link } from 'react-router-dom';
import '../App.css';
import SideBar from '../components/Topbar';
import GreetUser from '../components/GreetUser';
import { MyProSidebarProvider } from "../components/SidebarContext";
import { baseUrl } from '../components/BaseUrl';
import Footer from '../components/Footer';
import { paystackUrl } from '../components/PaystackUrl';
import code from '../assets/img/code.png';
import axios from 'axios';
import moment from 'moment';
import jsPDF from 'jspdf';
import img from '../assets/img/cert.png';
import Swal from "sweetalert2";
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Container, Row, Col, Card as BootstrapCard, Badge, ProgressBar } from 'react-bootstrap';
AOS.init();

// Engaging color palette - Psychology-based colors for mood and activity
const engagingColors = {
  // Primary - Trust & Productivity (Blue)
  primary: '#164cde',
  primaryLight: '#4895EF',
  primaryDark: '#3A0CA3',
  
  // Secondary - Growth & Progress (Teal)
  secondary: '#06D6A0',
  secondaryLight: '#7AE582',
  secondaryDark: '#04A777',
  
  // Energy - Action & Urgency (Amber)
  energy: '#FF9E00',
  energyLight: '#FFB74D',
  energyDark: '#FF9100',
  
  // Focus - Concentration & Learning (Deep Blue)
  focus: '#3A0CA3',
  focusLight: '#560BAD',
  focusDark: '#7209B7',
  
  // Success - Achievement & Completion (Green)
  success: '#06D6A0',
  successLight: '#7AE582',
  successDark: '#04A777',
  
  // Warning - Attention & Caution (Orange)
  warning: '#FF9E00',
  warningLight: '#FFB74D',
  warningDark: '#FF6B00',
  
  // Error - Urgent Action Needed (Coral)
  error: '#EF476F',
  errorLight: '#F48FB1',
  errorDark: '#E91E63',
  
  // Reward - Achievement & Celebration (Purple)
  reward: '#7209B7',
  rewardLight: '#9D4EDD',
  rewardDark: '#560BAD',
  
  // Calm - Neutral & Balanced (Gray-Blue)
  calm: '#4CC9F0',
  calmLight: '#56CFE1',
  calmDark: '#3A86FF',
  
  // Backgrounds & Surfaces
  background: '#FFFFFF',
  surface: '#F8F9FA',
  surfaceElevated: '#FFFFFF',
  text: '#2D3047',
  textMuted: '#6C757D',
  border: '#E9ECEF',
  
  // Gradients for visual interest
  gradientPrimary: 'linear-gradient(135deg, #4361EE 0%, #3A0CA3 100%)',
  gradientSuccess: 'linear-gradient(135deg, #06D6A0 0%, #04A777 100%)',
  gradientEnergy: 'linear-gradient(135deg, #FF9E00 0%, #FF6B00 100%)',
  gradientReward: 'linear-gradient(135deg, #7209B7 0%, #560BAD 100%)',
  gradientFocus: 'linear-gradient(135deg, #3A0CA3 0%, #560BAD 100%)'
};

// Styled components without animations
const ModernCard = styled(BootstrapCard)(({ theme, glow, interactive = true }) => ({
  border: 'none',
  borderRadius: '16px',
  background: engagingColors.surfaceElevated,
  boxShadow: glow ? '0 8px 25px rgba(67, 97, 238, 0.15)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: interactive ? 'all 0.3s ease' : 'none',
  position: 'relative',
  overflow: 'hidden',
  
  '&:hover': interactive ? {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
  } : {}
}));

const HolidayAlert = styled(Alert)(({ theme }) => ({
  borderRadius: '12px',
  border: 'none',
  background: 'linear-gradient(135deg, #FFD166, #FFB347)',
  color: engagingColors.text,
  fontWeight: '600',
  '& .MuiAlert-icon': {
    color: engagingColors.text
  }
}));

const AnnouncementCard = styled(ModernCard)(({ priority }) => ({
  borderLeft: `4px solid ${
    priority === 'high' ? engagingColors.error : 
    priority === 'medium' ? engagingColors.warning : 
    engagingColors.calm
  }`
}));

// Mood-based stat cards
const MoodStatCard = ({ title, value, icon, mood, subtitle, trend, loading = false }) => {
  const moodConfig = {
    productive: {
      color: engagingColors.primary,
      bgColor: alpha(engagingColors.primary, 0.1),
      gradient: engagingColors.gradientPrimary,
      iconBg: engagingColors.primary
    },
    growth: {
      color: engagingColors.success,
      bgColor: alpha(engagingColors.success, 0.1),
      gradient: engagingColors.gradientSuccess,
      iconBg: engagingColors.success
    },
    energy: {
      color: engagingColors.energy,
      bgColor: alpha(engagingColors.energy, 0.1),
      gradient: engagingColors.gradientEnergy,
      iconBg: engagingColors.energy
    },
    focus: {
      color: engagingColors.focus,
      bgColor: alpha(engagingColors.focus, 0.1),
      gradient: engagingColors.gradientFocus,
      iconBg: engagingColors.focus
    },
    reward: {
      color: engagingColors.reward,
      bgColor: alpha(engagingColors.reward, 0.1),
      gradient: engagingColors.gradientReward,
      iconBg: engagingColors.reward
    }
  };

  const config = moodConfig[mood] || moodConfig.productive;

  return (
    <ModernCard className="h-100">
      <BootstrapCard.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <span 
            className="small fw-semibold"
            style={{ 
              color: engagingColors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {title}
          </span>
          <div 
            className="rounded-2 p-2 d-flex align-items-center justify-content-center"
            style={{
              background: config.bgColor,
              color: config.color
            }}
          >
            {icon}
          </div>
        </div>
        
        {loading ? (
          <div className="d-flex justify-content-center">
            <Spinner size="sm" style={{ color: config.color }} />
          </div>
        ) : (
          <>
            <h3 
              className="fw-bold mb-2"
              style={{ 
                color: config.color,
                fontSize: '2rem'
              }}
            >
              {value}
            </h3>
            <div className="d-flex align-items-center gap-2">
              {trend && (
                <Badge 
                  style={{ 
                    backgroundColor: trend.includes('↑') ? alpha(engagingColors.success, 0.1) : alpha(engagingColors.warning, 0.1),
                    color: trend.includes('↑') ? engagingColors.success : engagingColors.warning
                  }}
                  className="fw-semibold small"
                >
                  {trend}
                </Badge>
              )}
              <span 
                className="small"
                style={{ color: engagingColors.textMuted }}
              >
                {subtitle}
              </span>
            </div>
          </>
        )}
      </BootstrapCard.Body>
    </ModernCard>
  );
};

// Activity-based course card
const ActivityCourseCard = ({ item, type = 'track', onPrintCertificate, activityLevel = 'medium' }) => {
  const isTrack = type === 'track';
  const score = isTrack ? null : parseInt(item.current_score) - 50;
  
  // Activity level determines color intensity
  const activityConfig = {
    high: {
      primary: engagingColors.energy,
      secondary: alpha(engagingColors.energy, 0.1),
      badge: engagingColors.energy
    },
    medium: {
      primary: engagingColors.primary,
      secondary: alpha(engagingColors.primary, 0.1),
      badge: engagingColors.primary
    },
    low: {
      primary: engagingColors.calm,
      secondary: alpha(engagingColors.calm, 0.1),
      badge: engagingColors.calm
    },
    completed: {
      primary: engagingColors.success,
      secondary: alpha(engagingColors.success, 0.1),
      badge: engagingColors.success
    }
  };

  const getActivityLevel = () => {
    if (isTrack) {
      return item.completion_status === "completed" ? 'completed' : 'medium';
    }
    if (score >= 90) return 'high';
    if (score >= 70) return 'medium';
    if (score >= 50) return 'low';
    return 'low';
  };

  const activity = getActivityLevel();
  const config = activityConfig[activity];
  const progressValue = isTrack ? (item.completion_status === "completed" ? 100 : 50) : score;

  const getPerformanceLevel = () => {
    if (isTrack) {
      return item.completion_status === "completed" ? "Mastered" : "In Progress";
    }
    if (score >= 90) return "Excellent";
    if (score >= 70) return "Good";
    if (score >= 50) return "Fair";
    return "Needs Practice";
  };

  return (
    <ModernCard className="h-100">
      <BootstrapCard.Body className="p-4">
        <div className="d-flex align-items-center gap-3">
          <div className="position-relative">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center position-relative"
              style={{
                width: '70px',
                height: '70px',
                background: `conic-gradient(${config.primary} ${progressValue * 3.6}deg, ${config.secondary} 0deg)`
              }}
            >
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: engagingColors.background,
                  position: 'absolute'
                }}
              >
                <span 
                  className="fw-bold"
                  style={{ color: config.primary, fontSize: '0.9rem' }}
                >
                  {isTrack ? (item.completion_status === "completed" ? '✓' : '50%') : `${score}%`}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex-grow-1">
            <h6 className="fw-bold mb-1" style={{ color: engagingColors.text, fontSize: '1rem', textTransform: 'capitalize' }}>
              {isTrack ? item.track_enrolled : item.course}
            </h6>
            
            <Badge 
              style={{
                backgroundColor: config.secondary,
                color: config.primary,
                border: `1px solid ${config.primary}`
              }}
              className="fw-semibold small mb-2"
            >
              {getPerformanceLevel()}
            </Badge>
            
            <div className="d-flex align-items-center justify-content-between mt-2">
              <span 
                className="small"
                style={{ color: engagingColors.textMuted }}
              >
                {item.date_enrolled || item.date_created ? 
                  moment(item.date_enrolled || item.date_created, "YYYYMMDD").fromNow() 
                  : null
                }
              </span>
              
              {!isTrack && (
                <Button 
                  variant="link" 
                  size="sm"
                  onClick={() => onPrintCertificate(
                    `${localStorage.getItem("codefussion-student-first-name")} ${localStorage.getItem("codefussion-student-last-name")}`,
                    `${item.course.toUpperCase()} Quiz With ${score}% Score`
                  )}
                  style={{ color: engagingColors.primary }}
                  className="p-0 fw-semibold"
                >
                  <Print fontSize="small" />
                </Button>
              )}
            </div>
          </div>
        </div>
        
        <Button
          variant="outline-primary"
          className="w-100 mt-3 rounded-2 d-flex align-items-center justify-content-between"
          style={{
            borderColor: config.secondary,
            color: config.primary,
            backgroundColor: config.secondary,
            fontSize: '0.8rem',
            padding: '0.75rem',
            fontWeight: '600'
          }}
        >
          {isTrack ? 'Continue Learning' : 'View Details'}
          <ChevronRight fontSize="small" />
        </Button>
      </BootstrapCard.Body>
    </ModernCard>
  );
};

// Mood-based subscription status
const MoodSubscriptionStatus = ({ status, onRefresh, loading = false }) => {
  const isActive = status === 'active';
  
  const statusConfig = {
    active: {
      color: engagingColors.success,
      bgColor: alpha(engagingColors.success, 0.1),
      icon: <CheckCircle />,
      text: 'Active'
    },
    inactive: {
      color: engagingColors.error,
      bgColor: alpha(engagingColors.error, 0.1),
      icon: <Cancel />,
      text: 'Inactive'
    }
  };

  const config = statusConfig[isActive ? 'active' : 'inactive'];

  return (
    <ModernCard className="mb-4">
      <BootstrapCard.Body className="p-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: '40px',
                height: '40px',
                backgroundColor: config.bgColor,
                color: config.color
              }}
            >
              {config.icon}
            </div>
            <div>
              <h6 className="fw-bold mb-1" style={{ color: engagingColors.text }}>
                Subscription Status
              </h6>
              <Badge 
                style={{
                  backgroundColor: config.bgColor,
                  color: config.color
                }}
                className="fw-semibold"
              >
                {config.text}
              </Badge>
            </div>
          </div>
          
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={onRefresh}
            disabled={loading}
            className="rounded-2"
            style={{
              borderColor: alpha(engagingColors.primary, 0.3),
              color: engagingColors.primary,
              fontWeight: '600'
            }}
          >
            {loading ? <Spinner size="sm" /> : 'Refresh Status'}
          </Button>
        </div>
        
        {!isActive && (
          <Alert 
            className="mt-3 rounded-2"
            style={{
              backgroundColor: alpha(engagingColors.warning, 0.05),
              border: `1px solid ${alpha(engagingColors.warning, 0.2)}`,
              color: engagingColors.text
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <Warning style={{ color: engagingColors.warning }} />
              <span className="small">
                Your subscription is inactive. <Link to="/subscription-plans" style={{ color: engagingColors.primary, fontWeight: '600' }}>Upgrade now</Link> to continue learning.
              </span>
            </div>
          </Alert>
        )}
      </BootstrapCard.Body>
    </ModernCard>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const [status, setStatus] = useState('');
  const [tracks, setTracks] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [courses, setCourses] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [dateEnrolled, setDateEnrolled] = useState([]);
  const [paymmentDate, setPaymentDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [plan_token, setToken] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [secretKey, setSecretKey] = useState('');
  
  // New states for announcements and holidays
  const [announcements, setAnnouncements] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [todayHoliday, setTodayHoliday] = useState(null);
  
  const token = localStorage.getItem("codefussion-login-token");

  // Generate certificate function
  const generateCertificate = (name, course) => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.addImage(img, 'PNG', 0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight());
    doc.setFontSize(36);
    doc.setFont('helvetica');
    doc.text(name, 146, 135, { align: 'center' });
    doc.setFontSize(20);
    doc.text(course, 135, 160, { align: 'center' });
    doc.save(`${name}.pdf`);
  };

  // Fetch secret key from backend
  const fetchSecretKey = async () => {
    try {
      const token = localStorage.getItem("codefussion-login-token");
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(`${baseUrl}get_secret_key.php`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        }
      });
      
      if (response.data.status === 1) {
        setSecretKey(response.data.secret_key);
        return response.data.secret_key;
      } else {
        throw new Error(response.data.message || 'Failed to get secret key');
      }
    } catch (error) {
      console.error('Error fetching secret key:', error);
      return null;
    }
  };

  // Check subscription from Paystack
  const checkPaystackSubscription = async (currentSecretKey) => {
    try {
      const response = await axios.get(
        `${paystackUrl}customer/${localStorage.getItem("codefussion-student-email")}`,
        { 
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentSecretKey}`
          }
        }
      );
      
      if (response.data.data && response.data.data.subscriptions && response.data.data.subscriptions.length > 0) {
        const activeSubscriptions = response.data.data.subscriptions.filter(
          sub => sub.status === 'active' || sub.status === 'non-renewing'
        );
        
        if (activeSubscriptions.length > 0) {
          return { 
            active: true, 
            subscriptions: activeSubscriptions,
            message: 'Active subscription found on Paystack'
          };
        }
      }
      
      return { 
        active: false, 
        message: 'No active subscriptions found on Paystack' 
      };
    } catch (error) {
      console.error('Error checking Paystack subscription:', error);
      return { 
        active: false, 
        message: 'Error checking Paystack subscription' 
      };
    }
  };

  // Check local database for subscription
  const checkLocalSubscription = async () => {
    try {
      const userData = {
        email: localStorage.getItem("codefussion-student-email"),
      };
      
      const response = await axios.post(`${baseUrl}get-payment-id.php`, userData, {
        headers: { 
          Accept: "application/json", 
          "Content-Type": "application/json", 
          Authorization: token 
        }
      });
      
      if (response.data.length > 0) {
        for (const item of response.data) {
          if (item.status === 1) {
            const date = item.date_created;
            const today = moment().startOf('day');
            const createdDate = moment(date, "YYYY-MM-DD").startOf('day');
            const diff = today.diff(createdDate, 'days');
            
            if (diff < 360) {
              return { 
                active: true, 
                plan: item.code,
                paymentData: item,
                message: 'Active subscription found in local database'
              };
            }
          }
        }
      }
      
      return { 
        active: false, 
        message: 'No active subscription found in local database' 
      };
    } catch (error) {
      console.error('Error checking local subscription:', error);
      return { 
        active: false, 
        message: 'Error checking local subscription' 
      };
    }
  };

  // Check Subscription Status from localStorage
  const checkSubscriptionStatus = () => {
    const status = localStorage.getItem("codefussion-subscription-status");
    setSubscriptionStatus(status || 'inactive');
    return status;
  };

  // Refresh subscription status with both checks
  const refreshSubscription = async () => {
    setLoading(true);
    try {
      let isActive = false;

      // Check local database first
      const localCheck = await checkLocalSubscription();
      
      if (localCheck.active) {
        isActive = true;
        localStorage.setItem("codefussion-subscription-status", "active");
        if (localCheck.plan) {
          localStorage.setItem("plan", localCheck.plan);
        }
      } else {
        // If local check fails, check Paystack
        const currentSecretKey = await fetchSecretKey();
        if (currentSecretKey) {
          const paystackCheck = await checkPaystackSubscription(currentSecretKey);
          
          if (paystackCheck.active) {
            isActive = true;
            localStorage.setItem("codefussion-subscription-status", "active");
          }
        }
      }

      setSubscriptionStatus(isActive ? 'active' : 'inactive');
      
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setSubscriptionStatus('inactive');
    } finally {
      setLoading(false);
    }
  };

  // Fetch announcements from database
  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${baseUrl}get-announcements.php`, {
        headers: { 
          Accept: "application/json", 
          "Content-Type": "application/json", 
          Authorization: token 
        }
      });
      if (response.data && response.data.length > 0) {
        setAnnouncements(response.data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncements([
        {
          id: 1,
          title: "New Course Available!",
          message: "Advanced React Patterns course is now available for enrollment.",
          date: moment().format('YYYY-MM-DD'),
          priority: "high"
        },
        {
          id: 2,
          title: "System Maintenance",
          message: "Scheduled maintenance this weekend. Platform will be unavailable for 2 hours.",
          date: moment().add(1, 'day').format('YYYY-MM-DD'),
          priority: "medium"
        }
      ]);
    }
  };

  // Fetch public holidays from database
  const fetchPublicHolidays = async () => {
    try {
      const response = await axios.get(`${baseUrl}get-public-holidays.php`, {
        headers: { 
          Accept: "application/json", 
          "Content-Type": "application/json", 
          Authorization: token 
        }
      });
      if (response.data && response.data.length > 0) {
        setPublicHolidays(response.data);
        checkTodayHoliday(response.data);
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
      const sampleHolidays = [
        {
          id: 1,
          name: "New Year's Day",
          date: moment().format('YYYY-01-01'),
          country: "Global"
        },
        {
          id: 2,
          name: "Christmas Day",
          date: moment().format('YYYY-12-25'),
          country: "Global"
        }
      ];
      setPublicHolidays(sampleHolidays);
      checkTodayHoliday(sampleHolidays);
    }
  };

  // Check if today is a public holiday
  const checkTodayHoliday = (holidays) => {
    const today = moment().format('YYYY-MM-DD');
    const holiday = holidays.find(h => h.date === today);
    if (holiday) {
      setTodayHoliday(holiday);
    }
  };

  // Memoized statistics
  const stats = useMemo(() => ({
    completedCourses: tracks.length === 0 ? 0 : completed,
    inProgressCourses: tracks.length === 0 ? 0 : inProgress,
    totalCourses: tracks.length,
    completionRate: tracks.length > 0 ? Math.round((completed / tracks.length) * 100) : 0
  }), [tracks, completed, inProgress]);

  // Data fetching functions
  const fetchEnrollment = async () => {
    setLoading(true);
    const userData = { email: localStorage.getItem("codefussion-student-email").trim() };
    try {
      const response = await axios.post(`${baseUrl}get-enrolled-student.php`, userData, {
        headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: token }
      });
      
      if (response.data.length > 0) {
        setTracks(response.data);
        response.data.forEach((item) => {
          const dataProgress = [];
          dataProgress.push(item.completion_status);
          const numProgress = dataProgress.filter(val => val === 'in progress').length;
          const numCompleted = dataProgress.filter(val => val === 'completed').length;
          setInProgress(numProgress);
          setCompleted(numCompleted);
          setCourses(item.courses);
          setDateEnrolled(item.date_enrolled);
        });
      }
    } catch (error) {
      console.error('Error fetching enrollment:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuizScore = async () => {
    setLoading(true);
    const userData = { email: localStorage.getItem("codefussion-student-email") };
    try {
      const response = await axios.post(`${baseUrl}get-quiz-score.php`, userData, {
        headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: token }
      });
      if (response.data.length > 0) {
        setQuiz(response.data);
      }
    } catch (error) {
      console.error('Error fetching quiz scores:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnrollment();
    fetchQuizScore();
    checkSubscriptionStatus();
    fetchAnnouncements();
    fetchPublicHolidays();
  }, []);

  if (localStorage.getItem("codefussion-login-token") === null) {
    window.location.replace("/login");
    return null;
  }

  return (
    <MyProSidebarProvider>
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-2 col-md-2 p-0">
            <SideBar />
          </div>
          <div className="col-lg-12 col-md-10" style={{ display: 'flex', alignSelf: 'flex-start'}}>
            <Container fluid className="py-4" style={{ background: engagingColors.background, minHeight: '100vh' }}>
              
              {/* Public Holiday Alert */}
              {todayHoliday && (
                <HolidayAlert className="mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Public Holiday Today!</strong> - {todayHoliday.name}
                    </div>
                    <Badge style={{ backgroundColor: alpha(engagingColors.warning, 0.2), color: engagingColors.text }}>
                      {todayHoliday.country}
                    </Badge>
                  </div>
                </HolidayAlert>
              )}

              {/* Header Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="fw-bold mb-2" style={{ color: engagingColors.text }}>
                      Welcome back, {localStorage.getItem("codefussion-student-first-name")}
                    </h2>
                    <p className="mb-0" style={{ color: engagingColors.textMuted }}>
                      Here's your learning progress summary
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    <MuiBadge badgeContent={announcements.length} color="error">
                      <Notifications style={{ color: engagingColors.primary }} />
                    </MuiBadge>
                  </div>
                </div>
              </div>

              {/* Subscription Status */}
              <MoodSubscriptionStatus 
                status={subscriptionStatus} 
                onRefresh={refreshSubscription}
                loading={loading}
              />

              {/* Statistics Grid */}
              <Row className="g-3 mb-4">
                <Col xs={12} sm={6} md={3}>
                  <MoodStatCard
                    title="Completed Courses"
                    value={stats.completedCourses}
                    icon={<School style={{ fontSize: 24 }} />}
                    mood="success"
                    trend={`↑ ${stats.completionRate}%`}
                    subtitle="Great progress!"
                    loading={loading}
                  />
                </Col>
                
                <Col xs={12} sm={6} md={3}>
                  <MoodStatCard
                    title="In Progress"
                    value={stats.inProgressCourses}
                    icon={<TrendingUp style={{ fontSize: 24 }} />}
                    mood="energy"
                    trend="↗ Ongoing"
                    subtitle="Keep learning!"
                    loading={loading}
                  />
                </Col>
                
                <Col xs={12} sm={6} md={3}>
                  <MoodStatCard
                    title="Total Courses"
                    value={stats.totalCourses}
                    icon={<Assessment style={{ fontSize: 24 }} />}
                    mood="productive"
                    trend="Active"
                    subtitle="Your journey"
                    loading={loading}
                  />
                </Col>
                
                <Col xs={12} sm={6} md={3}>
                  <ModernCard className="h-100 text-center">
                    <BootstrapCard.Body className="p-4">
                      <div
                        className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                        style={{
                          background: engagingColors.gradientReward,
                          color: 'white',
                          width: '60px',
                          height: '60px'
                        }}
                      >
                        <EmojiEvents style={{ fontSize: 28 }} />
                      </div>
                      <h4 className="fw-bold mb-1" style={{ color: engagingColors.reward }}>
                        {stats.completionRate}%
                      </h4>
                      <span style={{ color: engagingColors.textMuted }}>Completion Rate</span>
                    </BootstrapCard.Body>
                  </ModernCard>
                </Col>
              </Row>

              {/* Main Content Grid */}
              <Row className="g-4">
                {/* Left Column - Learning Progress & Announcements */}
                <Col lg={8}>
                  {/* Announcements Section */}
                  <ModernCard className="mb-4">
                    <BootstrapCard.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="fw-bold mb-0" style={{ color: engagingColors.text }}>
                          <Announcement className="me-2" />
                          Latest Announcements
                        </h4>
                        <Badge style={{ backgroundColor: engagingColors.primary, color: 'white' }}>
                          {announcements.length} New
                        </Badge>
                      </div>
                      
                      <div className="d-flex flex-column gap-3">
                        {announcements.length === 0 ? (
                          <div className="text-center py-4">
                            <Announcement style={{ fontSize: 48, color: engagingColors.textMuted, marginBottom: '1rem' }} />
                            <h5 style={{ color: engagingColors.textMuted, marginBottom: '1rem' }}>No announcements yet</h5>
                            <p style={{ color: engagingColors.textMuted }}>Check back later for updates</p>
                          </div>
                        ) : (
                          announcements.map((announcement) => (
                            <AnnouncementCard 
                              key={announcement.id}
                              priority={announcement.priority}
                              className="p-3"
                            >
                              <div className="d-flex align-items-start gap-3">
                                <div 
                                  className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                  style={{
                                    backgroundColor: 
                                      announcement.priority === 'high' ? alpha(engagingColors.error, 0.1) :
                                      announcement.priority === 'medium' ? alpha(engagingColors.warning, 0.1) :
                                      alpha(engagingColors.calm, 0.1),
                                    color: 
                                      announcement.priority === 'high' ? engagingColors.error :
                                      announcement.priority === 'medium' ? engagingColors.warning :
                                      engagingColors.calm
                                  }}
                                >
                                  <Notifications fontSize="small" />
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="fw-bold mb-1" style={{ color: engagingColors.text }}>
                                    {announcement.title}
                                  </h6>
                                  <p className="small mb-2" style={{ color: engagingColors.textMuted }}>
                                    {announcement.message}
                                  </p>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <Badge 
                                      style={{
                                        backgroundColor: 
                                          announcement.priority === 'high' ? alpha(engagingColors.error, 0.1) :
                                          announcement.priority === 'medium' ? alpha(engagingColors.warning, 0.1) :
                                          alpha(engagingColors.calm, 0.1),
                                        color: 
                                          announcement.priority === 'high' ? engagingColors.error :
                                          announcement.priority === 'medium' ? engagingColors.warning :
                                          engagingColors.calm
                                      }}
                                      className="small"
                                    >
                                      {announcement.priority}
                                    </Badge>
                                    <span className="small" style={{ color: engagingColors.textMuted }}>
                                      {moment(announcement.date).fromNow()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </AnnouncementCard>
                          ))
                        )}
                      </div>
                    </BootstrapCard.Body>
                  </ModernCard>

                  {/* Learning Tracks */}
                  <ModernCard className="mb-4">
                    <BootstrapCard.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="fw-bold mb-0" style={{ color: engagingColors.text }}>
                          Your Learning Tracks
                        </h4>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="rounded-2"
                          style={{
                            borderColor: alpha(engagingColors.primary, 0.3),
                            color: engagingColors.primary,
                            fontWeight: '600'
                          }}
                        >
                          View All
                        </Button>
                      </div>
                      
                      <Row className="g-3">
                        {tracks.length === 0 ? (
                          <Col xs={12}>
                            <div className="text-center py-5">
                              <School style={{ fontSize: 48, color: engagingColors.textMuted, marginBottom: '1rem' }} />
                              <h5 style={{ color: engagingColors.textMuted, marginBottom: '1rem' }}>No active courses yet</h5>
                              <Button 
                                variant="light"
                                style={{
                                  background: engagingColors.primary,
                                  border: 'none',
                                  color: '#fff',
                                  fontWeight: '600'
                                }}
                                className="rounded-2 px-4"
                              >
                                Start Learning
                              </Button>
                            </div>
                          </Col>
                        ) : (
                          tracks.map((item, index) => (
                            <Col xs={12} md={6} key={index}>
                              <ActivityCourseCard 
                                item={item} 
                                type="track" 
                                onPrintCertificate={generateCertificate} 
                              />
                            </Col>
                          ))
                        )}
                      </Row>
                    </BootstrapCard.Body>
                  </ModernCard>

                  {/* Quiz Results */}
                  <ModernCard>
                    <BootstrapCard.Body className="p-4">
                      <h4 className="fw-bold mb-4" style={{ color: engagingColors.text }}>
                        Quiz Results
                      </h4>
                      
                      <Row className="g-3">
                        {quiz.length === 0 ? (
                          <Col xs={12}>
                            <div className="text-center py-4">
                              <EmojiEvents style={{ fontSize: 48, color: engagingColors.textMuted, marginBottom: '1rem' }} />
                              <h5 style={{ color: engagingColors.textMuted, marginBottom: '1rem' }}>No quiz results yet</h5>
                              <Link to="/quiz">
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  style={{
                                    background: engagingColors.gradientPrimary,
                                    border: 'none',
                                    fontWeight: '600'
                                  }}
                                  className="rounded-2"
                                >
                                  Take a Quiz
                                </Button>
                              </Link>
                            </div>
                          </Col>
                        ) : (
                          quiz.map((item, index) => (
                            <Col xs={12} md={6} key={index}>
                              <ActivityCourseCard 
                                item={item} 
                                type="quiz" 
                                onPrintCertificate={generateCertificate} 
                              />
                            </Col>
                          ))
                        )}
                      </Row>
                    </BootstrapCard.Body>
                  </ModernCard>
                </Col>

                {/* Right Column - Sidebar */}
                <Col lg={4}>
                  {/* Public Holidays */}
                  <ModernCard className="mb-4">
                    <BootstrapCard.Body className="p-4">
                      <h5 className="fw-bold mb-3" style={{ color: engagingColors.text }}>
                        <Event className="me-2" />
                        Upcoming Holidays
                      </h5>
                      
                      <div className="d-flex flex-column gap-2">
                        {publicHolidays.length === 0 ? (
                          <div className="text-center py-3">
                            <Celebration style={{ fontSize: 32, color: engagingColors.textMuted, marginBottom: '0.5rem' }} />
                            <p className="small" style={{ color: engagingColors.textMuted }}>No upcoming holidays</p>
                          </div>
                        ) : (
                          publicHolidays.slice(0, 5).map((holiday) => (
                            <div 
                              key={holiday.id}
                              className="d-flex align-items-center justify-content-between p-3 rounded-2"
                              style={{
                                backgroundColor: alpha(engagingColors.energy, 0.05),
                                border: `1px solid ${alpha(engagingColors.energy, 0.1)}`
                              }}
                            >
                              <div>
                                <h6 className="fw-semibold mb-1 small" style={{ color: engagingColors.text }}>
                                  {holiday.name}
                                </h6>
                                <span className="small" style={{ color: engagingColors.textMuted }}>{holiday.country}</span>
                              </div>
                              <Badge 
                                style={{
                                  backgroundColor: alpha(engagingColors.energy, 0.1),
                                  color: engagingColors.energy
                                }}
                                className="small"
                              >
                                {moment(holiday.date).format('MMM D')}
                              </Badge>
                            </div>
                          ))
                        )}
                      </div>
                    </BootstrapCard.Body>
                  </ModernCard>

                  {/* Study Analytics */}
                  <ModernCard className="mb-4">
                    <BootstrapCard.Body className="p-4">
                      <h5 className="fw-bold mb-3" style={{ color: engagingColors.text }}>
                        Study Analytics
                      </h5>
                      <LineChart
                        xAxis={[{ data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] }]}
                        series={[{
                          data: [2, 3, 5.5, 8.5, 1.5, 5, 1, 4, 3, 8],
                          showMark: ({ index }) => index % 2 === 0,
                          color: engagingColors.primary,
                          area: true,
                        }]}
                        height={200}
                        sx={{
                          '.MuiLineElement-root': {
                            strokeWidth: 3,
                          },
                          '.MuiAreaElement-root': {
                            fill: `url(#gradient)`,
                          },
                        }}
                        slotProps={{
                          legend: {
                            hidden: true,
                          },
                        }}
                      >
                        <defs>
                          <linearGradient id="gradient" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="5%" stopColor={engagingColors.primary} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={engagingColors.primary} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                      </LineChart>
                    </BootstrapCard.Body>
                  </ModernCard>

                  {/* Quick Actions */}
                  <ModernCard>
                    <BootstrapCard.Body className="p-4">
                      <h5 className="fw-bold mb-3" style={{ color: engagingColors.text }}>
                        Quick Actions
                      </h5>
                      
                      <div className="d-flex flex-column gap-2">
                        <Button
                          variant="outline-primary"
                          className="rounded-2 py-2 text-start d-flex align-items-center"
                          style={{
                            borderColor: alpha(engagingColors.primary, 0.2),
                            color: engagingColors.primary,
                            fontWeight: '600'
                          }}
                        >
                          <PlayArrow className="me-2" />
                          Continue Learning
                        </Button>
                        
                        <Button
                          variant="outline-primary"
                          className="rounded-2 py-2 text-start d-flex align-items-center"
                          style={{
                            borderColor: alpha(engagingColors.primary, 0.2),
                            color: engagingColors.primary,
                            fontWeight: '600'
                          }}
                        >
                          <Bookmark className="me-2" />
                          Saved Courses
                        </Button>
                        
                        <Button
                          variant="outline-primary"
                          className="rounded-2 py-2 text-start d-flex align-items-center"
                          style={{
                            borderColor: alpha(engagingColors.primary, 0.2),
                            color: engagingColors.primary,
                            fontWeight: '600'
                          }}
                        >
                          <People className="me-2" />
                          Study Groups
                        </Button>
                      </div>
                    </BootstrapCard.Body>
                  </ModernCard>
                </Col>
              </Row>

              {/* WhatsApp Float Button */}
              <Button
                as="a"
                href="https://api.whatsapp.com/send?phone=+2347089386320&text=Hello"
                target="_blank"
                variant="primary"
                style={{
                  position: 'fixed',
                  bottom: '24px',
                  right: '24px',
                  background: engagingColors.gradientPrimary,
                  border: 'none',
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000
                }}
                className="whatsapp-btn"
              >
                <WhatsApp style={{ color: 'white' }} />
              </Button>
            </Container>
          </div>
        </div>
      </div>

      <style jsx>{`
        .whatsapp-btn:hover {
          transform: scale(1.05);
          transition: all 0.3s ease;
        }
      `}</style>
    </MyProSidebarProvider>
  );
};

export default Dashboard;